import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils, EMPTY_PROJECT_DETAIL } from '../../utils/commonUtils';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import { getCategoryGroupDetail, updateCategoryGroup, CategoryGroupDetail } from '../../utils/productCategoryGroup';
import { CategoryGroupFormUtils, FormData, FormErrors } from '../../utils/productCategoryGroupCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { 
  Save,
  ArrowLeft,
  RefreshCw,
  Info,
  AlertCircle,
  X,
  History
} from 'lucide-react';

const CategoryGroupUpdate: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const { categoryGroupCode } = useParams<{ categoryGroupCode: string }>();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-category-group-list');
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [canEditCode, setCanEditCode] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>(CategoryGroupFormUtils.getDefaultFormData());
  const [errors, setErrors] = useState<FormErrors>({});
  const [originalData, setOriginalData] = useState<FormData | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [categoryGroupMetadata, setCategoryGroupMetadata] = useState<{
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  // Check if screen is mobile size
  CategoryGroupFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = CategoryGroupFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) {
        return;
      }

      try {
        setIsLoadingBrands(true);
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          console.log('프로젝트 코드가 없습니다.');
          return;
        }

        console.log('평탄화된 브랜드 목록 API 호출:', { projectCode });
        const response = await getBrandListFlat(projectCode);
        console.log('평탄화된 브랜드 목록 API 응답:', response);

        if (response && response.brands) {
          setBrands(response.brands);
          console.log('설정된 브랜드 목록:', response.brands);
        } else {
          console.warn('응답에 brands 데이터가 없습니다:', response);
          setBrands([]);
        }
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    };

    if (projectDetail) {
      fetchBrands();
    }
  }, [projectDetail]);

  // 카테고리/그룹 상세 정보 로드
  useEffect(() => {
    const loadCategoryGroupDetail = async () => {
      if (!categoryGroupCode) {
        setLoadError('카테고리/그룹 코드가 없습니다.');
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        setLoadError(null);
        
        const projectCode = getProjectCode();
        const categoryGroup = await getCategoryGroupDetail(projectCode, categoryGroupCode);

        // API 응답을 FormData 형태로 변환
        const categoryGroupFormData: FormData = {
          brandCode: categoryGroup.brandCode || '',
          code: categoryGroup.code,
          status: categoryGroup.status,
          type: categoryGroup.type,
          title: categoryGroup.title,
          subtitle: categoryGroup.subtitle || '',
          description: categoryGroup.description || ''
        };
        
        setFormData(categoryGroupFormData);
        setOriginalData(categoryGroupFormData);
        
        // 메타데이터 저장
        if (categoryGroup.createdAt && categoryGroup.createdBy && categoryGroup.updatedAt && categoryGroup.updatedBy) {
          setCategoryGroupMetadata({
            createdAt: categoryGroup.createdAt,
            createdBy: categoryGroup.createdBy,
            updatedAt: categoryGroup.updatedAt,
            updatedBy: categoryGroup.updatedBy
          });
        }
        
        // 생성시각과 수정시각이 동일한 경우(최초 생성 시)에만 코드 수정 가능
        const canEdit = categoryGroup.createdAt === categoryGroup.updatedAt;
        setCanEditCode(canEdit);
      } catch (error: any) {
        console.error('카테고리/그룹 상세 정보 로드 오류:', error);
        const displayMessage = CommonUtils.getNetworkErrorMessage(error);
        setLoadError(displayMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategoryGroupDetail();
  }, [categoryGroupCode]);

  // Track changes
  useEffect(() => {
    if (originalData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(changed);
    }
  }, [formData, originalData]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    CategoryGroupFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors = CategoryGroupFormUtils.validateUpdateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    await CategoryGroupFormUtils.handleUpdateSubmit(
      e,
      formData,
      validateForm,
      setIsSubmitting,
      setSubmitError,
      setHasChanges,
      setOriginalData,
      getProjectCode,
      categoryGroupCode!,
      updateCategoryGroup,
      navigate,
      location
    );
  };

  // Reset form to original data
  const handleReset = () => {
    if (originalData) {
      setFormData({...originalData});
    }
    setErrors({});
    setHasChanges(false);
    setShowResetConfirm(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-gray-600">카테고리/그룹 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-900 mb-2">오류 발생</h3>
            <p className="text-red-700 mb-4">{loadError}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                다시 시도
              </button>
              <button
                onClick={() => navigate('/products/category-groups')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar 
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail || EMPTY_PROJECT_DETAIL}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar 
          title="카테고리/그룹 수정" 
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail || undefined}
        />

        {/* Edit Category Group Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Back Button and Status */}
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => navigate('/products/category-groups')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                카테고리/그룹 목록으로
              </button>
              
              {hasChanges && (
                <span className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  수정사항 있음
                </span>
              )}
            </div>

            {/* Category Group Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {formData.title} ({formData.code})
                    {canEditCode && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        코드 수정 가능
                      </span>
                    )}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                    {categoryGroupMetadata && (
                      <>
                        <div className="flex items-center">
                          <History className="w-4 h-4 mr-2" />
                          생성: {formatDateTimeFromApi(categoryGroupMetadata.createdAt) || categoryGroupMetadata.createdAt} ({categoryGroupMetadata.createdBy})
                        </div>
                        <div className="flex items-center">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          수정: {formatDateTimeFromApi(categoryGroupMetadata.updatedAt) || categoryGroupMetadata.updatedAt} ({categoryGroupMetadata.updatedBy})
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-red-700">{submitError}</span>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 기본 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <Info className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">기본 정보</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Brand Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canEditCode ? 'bg-gray-50 text-gray-500' : ''}`}
                      disabled={isLoadingBrands || !canEditCode}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        const nbsp = '\u00A0';
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        const displayName = `${indent}${prefix}${brand.name}`;
                        
                        return (
                          <option key={brand.code} value={brand.code}>
                            {displayName}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.brandCode}</p>
                    )}
                    {!canEditCode && (
                      <p className="mt-1 text-xs text-gray-500">
                        브랜드는 최초 생성 시에만 수정할 수 있습니다.
                      </p>
                    )}
                  </div>

                  {/* Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      코드
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      maxLength={50}
                      title="영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 입력 가능"
                      disabled={!canEditCode}
                      className={`w-full px-3 py-2 border ${errors.code ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canEditCode ? 'bg-gray-50 text-gray-500' : ''}`}
                      placeholder="영문, 숫자, _, - 만 입력"
                    />
                    {errors.code && <p className="mt-1 text-xs text-red-500">{errors.code}</p>}
                    {!canEditCode && (
                      <p className="mt-1 text-xs text-gray-500">
                        코드는 최초 생성 시에만 수정할 수 있습니다.
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.status ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="PENDING">대기중</option>
                      <option value="ARCHIVED">보관됨</option>
                    </select>
                    {errors.status && (
                      <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                    )}
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.type ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">타입을 선택하세요</option>
                      <option value="CATALOG">카탈로그</option>
                      <option value="MODULE">화면 모듈</option>
                      <option value="MOBILE_ORDER">모바일 오더 카테고리</option>
                      {/* <option value="WEB_ORDER">웹 오더 카테고리</option>
                      <option value="KIOSK">키오스크 카테고리</option> */}
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                    )}
                  </div>

                  {/* Title */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      명칭 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      maxLength={50}
                      className={`w-full px-3 py-2 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="명칭을 입력하세요"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  {/* Subtitle */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      부제
                    </label>
                    <input
                      type="text"
                      name="subtitle"
                      value={formData.subtitle || ''}
                      onChange={handleInputChange}
                      maxLength={100}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="부제를 입력하세요"
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      설명
                    </label>
                    <textarea
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="설명을 입력하세요"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center ${!hasChanges && 'opacity-50 cursor-not-allowed'}`}
                  disabled={!hasChanges}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center ${(!hasChanges || isSubmitting) && 'opacity-50 cursor-not-allowed'}`}
                    disabled={!hasChanges || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        저장 중...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        변경사항 저장
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">변경사항 초기화</h3>
            </div>
            <p className="text-gray-600 mb-6">
              모든 변경사항이 초기화됩니다. 계속하시겠습니까?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryGroupUpdate;
